import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getLocalStorage, setLocalStorage } from 'services/common/storage';
import { getPageStaticAll } from 'services/pages';
import getDataSystemService from 'services/systems';
import { SystemDataTypes } from 'services/systems/types';
import { LOCAL_STORAGE } from 'utils/constants';

interface ShareModal {
  isOpen: boolean;
  url: string;
}

interface RecruitmentFooterModal {
  registerModal: boolean;
  positionModal: boolean;
}

interface OpenRecruitmentModal {
  isOpen: boolean;
  message: string;
}

interface SystemState {
  theme: 'light' | 'dark';
  loading: boolean;
  dataSystem?: SystemDataTypes;
  isDeveloping: boolean;
  isSearchAll: boolean;
  isChangeLanguage: boolean;
  language: string;
  pageTranslation?: TranslationDataTypes[];
  developMessage: string;
  modalOpen: {
    tokenExpired: boolean;
  }
  staticPages: StaticSlug[];
  shareModal: ShareModal;
  isOpenRecruitmentModal: OpenRecruitmentModal;
  isOpenUploadCVModal: boolean;
  recruitmentFooterModal: RecruitmentFooterModal;
  ekycErrorModal: OpenRecruitmentModal;
  bondMethod?: number; // 0: Bán; 1: Mua
  initializedGA: boolean;
}

const initialState: SystemState = {
  theme: getLocalStorage(LOCAL_STORAGE.AC_THEME) as ('light' | 'dark') || 'light',
  loading: false,
  isDeveloping: false,
  isSearchAll: false,
  isChangeLanguage: false,
  language: getLocalStorage(LOCAL_STORAGE.LANGUAGE) || 'vi',
  pageTranslation: undefined,
  bondMethod: undefined,
  developMessage: 'Chúng tôi đang xây dựng tính năng này. Quý khách vui lòng thử lại sau.',
  modalOpen: {
    tokenExpired: false,
  },
  staticPages: [],
  shareModal: {
    isOpen: false,
    url: '',
  },
  isOpenRecruitmentModal: {
    isOpen: false,
    message: '',
  },
  isOpenUploadCVModal: false,
  recruitmentFooterModal: {
    registerModal: false,
    positionModal: false,
  },
  ekycErrorModal: {
    isOpen: false,
    message: '',
  },
  initializedGA: false,
};

export const getSystemDataAction = createAsyncThunk<SystemDataTypes>(
  'systemReducer/getSystem',
  async (_, { rejectWithValue }) => {
    try {
      return await getDataSystemService();
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getPageStaticAllAction = createAsyncThunk<StaticSlug[]>(
  'systemReducer/getPageStaticAllAction',
  async (_, { rejectWithValue }) => {
    try {
      return await getPageStaticAll();
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const systemSlice = createSlice({
  name: 'systemReducer',
  initialState,
  reducers: {
    toggleEkycErrorModal: ($state, action: PayloadAction<OpenRecruitmentModal>) => {
      $state.ekycErrorModal = action.payload;
    },
    toggleLoading: ($state, action: PayloadAction<boolean>) => {
      $state.loading = action.payload;
    },
    toggleShareModal: ($state, action: PayloadAction<ShareModal>) => {
      $state.shareModal = action.payload;
    },
    toggleRecruitmentModal: ($state, action: PayloadAction<OpenRecruitmentModal>) => {
      $state.isOpenRecruitmentModal = action.payload;
    },
    toggleUploadCVModal: ($state, action: PayloadAction<boolean>) => {
      $state.isOpenUploadCVModal = action.payload;
    },
    toggleRecruitmentFooterModal: ($state, action: PayloadAction<RecruitmentFooterModal>) => {
      $state.recruitmentFooterModal = action.payload;
    },
    toggleThemeAction($state) {
      if ($state.theme === 'light') {
        $state.theme = 'dark';
        setLocalStorage(LOCAL_STORAGE.AC_THEME, 'dark');
      } else {
        $state.theme = 'light';
        setLocalStorage(LOCAL_STORAGE.AC_THEME, 'light');
      }
    },
    toggleIsDeveloping: ($state, action: PayloadAction<boolean>) => {
      $state.isDeveloping = action.payload;
    },
    toggleIsSearchAll: ($state, action: PayloadAction<boolean>) => {
      $state.isSearchAll = action.payload;
    },
    toggleIsChangeLanguage: ($state, action: PayloadAction<boolean>) => {
      $state.isChangeLanguage = action.payload;
    },
    setLanguage($state, action: PayloadAction<string>) {
      $state.language = action.payload;
      setLocalStorage(LOCAL_STORAGE.LANGUAGE, action.payload);
      $state.isChangeLanguage = true;
    },
    setPageTranslation($state, action: PayloadAction<TranslationDataTypes[] | undefined>) {
      $state.pageTranslation = action.payload;
    },
    setDevelopMessage($state, action: PayloadAction<string>) {
      $state.developMessage = action.payload;
    },
    resetDevelopMessage($state) {
      $state.developMessage = 'Chúng tôi đang xây dựng tính năng này. Quý khách vui lòng thử lại sau.';
    },
    toggleTokenExpired: ($state, action: PayloadAction<boolean>) => {
      $state.modalOpen = { ...$state.modalOpen, tokenExpired: action.payload };
    },
    setBondMethodTrading($state, action: PayloadAction<number>) {
      $state.bondMethod = action.payload;
    },
    setInitializedGA: ($state, action: PayloadAction<boolean>) => {
      $state.initializedGA = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getSystemDataAction.fulfilled, ($state, action) => {
      $state.dataSystem = action.payload;
    });
    builder.addCase(getPageStaticAllAction.fulfilled, ($state, action) => {
      $state.staticPages = action.payload;
    });
  },
});

export const {
  toggleLoading, toggleThemeAction,
  toggleIsDeveloping, toggleIsSearchAll,
  toggleIsChangeLanguage,
  setLanguage, setPageTranslation,
  setDevelopMessage, resetDevelopMessage,
  toggleTokenExpired,
  toggleShareModal,
  toggleRecruitmentModal,
  toggleUploadCVModal,
  toggleRecruitmentFooterModal,
  setBondMethodTrading,
  toggleEkycErrorModal,
  setInitializedGA,
} = systemSlice.actions;

export default systemSlice.reducer;
